"use client";

import React from "react";
import Link from "next/link";
import { UserPlus, LogIn } from "lucide-react";
import { useSafeAuth } from "@/hooks/use-safe-auth";

/**
 * Auth call-to-action pinned at the bottom of the mobile drawer.
 *
 * The mobile header only carries a compact login icon, so a guest had no
 * visible path to *register* — and most users sign up on a phone. This
 * gives guests a prominent "Create account" (signup) primary button plus a
 * secondary "Sign in", right where the drawer's account actions belong.
 *
 * Renders nothing for signed-in users (their profile / sign-out live in the
 * header avatar menu). During the auth probe we optimistically treat the
 * visitor as a guest and show the CTA immediately — `useSafeAuth` re-fetches
 * on every mount (each drawer open), so gating on `isLoading` would delay the
 * signup CTA every time; guests are the priority audience, so we only HIDE
 * once we KNOW the user is authenticated.
 */
export function MobileDrawerAuth({
  language,
  onNavigate,
}: {
  language: "en" | "mm";
  /** Close the drawer when a link is tapped (nav happens right after). */
  onNavigate?: () => void;
}) {
  const { isAuthenticated } = useSafeAuth();

  if (isAuthenticated) return null;

  return (
    <div className="border-t border-border p-3 flex-shrink-0 space-y-2">
      <Link
        href="/signup"
        onClick={onNavigate}
        className="flex items-center justify-center gap-2 w-full rounded-md px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
        style={{ backgroundColor: "var(--color-primary)" }}
      >
        <UserPlus size={16} />
        {language === "mm" ? "အကောင့်အသစ်ဖွင့်ရန်" : "Create account"}
      </Link>
      <Link
        href="/login"
        onClick={onNavigate}
        className="flex items-center justify-center gap-2 w-full rounded-md border px-4 py-2.5 text-sm font-medium transition-colors hover:bg-muted/60"
        style={{
          borderColor: "var(--color-primary)",
          color: "var(--color-primary)",
        }}
      >
        <LogIn size={16} />
        {language === "mm" ? "လော့ဂ်အင်ဝင်ရန်" : "Sign in"}
      </Link>
    </div>
  );
}

export default MobileDrawerAuth;
